import { useState } from 'react';
import { EmailTextSelect, Pill, cn, formatDkk } from '@jywer/ui';
import type { BillingInvoice } from './types';
import { cancelInvoice, editInvoice } from './api';

export type DraftActionKind = 'approve_and_send' | 'approve_only';
export interface DraftActionButton {
  kind: DraftActionKind;
  label: string;
  variant: 'primary' | 'secondary';
}

/**
 * Returns the button list for draft/postponed rows. Primary is the
 * combined approve+send (the common solo-Daniel case); secondary is
 * approve-only (sets up the bookkeeper handoff).
 */
export const DRAFT_ACTION_BUTTONS: readonly DraftActionButton[] = [
  { kind: 'approve_and_send', label: 'Godkend & marker som sendt', variant: 'primary' },
  { kind: 'approve_only', label: 'Kun godkend', variant: 'secondary' },
];

export function InvoiceRowExpansion({
  invoice,
  onChanged,
  onPostpone,
  onApprove,
  onApproveAndSend,
}: {
  invoice: BillingInvoice;
  onChanged: () => Promise<void> | void;
  onPostpone: () => void;
  onApprove: () => Promise<void> | void;
  onApproveAndSend: () => Promise<void> | void;
}) {
  const editable = invoice.status === 'draft' || invoice.status === 'postponed';
  const [amount, setAmount] = useState(invoice.amount);
  const [description, setDescription] = useState(invoice.description ?? '');
  const [emailText, setEmailText] = useState<string | null>(invoice.email_custom_text);
  const [saving, setSaving] = useState<false | 'save' | 'cancel'>(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving('save');
    setError(null);
    try {
      await editInvoice(invoice.invoice_id, {
        amount,
        description: description || null,
        email_custom_text: emailText,
      });
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ukendt fejl');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!confirm('Annullér denne faktura? Handlingen kan ikke fortrydes.')) return;
    setSaving('cancel');
    setError(null);
    try {
      await cancelInvoice(invoice.invoice_id);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ukendt fejl');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {editable ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-2">
              Beløb (DKK)
            </span>
            <input
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded border border-line-2 px-2 py-1.5"
            />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-2">
              Beskrivelse
            </span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded border border-line-2 px-2 py-1.5"
            />
          </label>
          <div className="block text-sm md:col-span-3">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-2">
              Ekstra e-mail-tekst
            </span>
            <EmailTextSelect value={emailText} onChange={setEmailText} size="md" />
          </div>
        </div>
      ) : null}

      {invoice.line_items && invoice.line_items.length > 0 ? (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-ink-2">Linjer</div>
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="border-b border-line-1 text-left text-xs text-ink-2">
                <th className="py-1">Beskrivelse</th>
                <th className="py-1 text-right">Antal</th>
                <th className="py-1 text-right">Beløb</th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items.map((l) => (
                <tr key={l.id} className="border-b border-line-1">
                  <td className="py-1">{l.description}</td>
                  <td className="py-1 text-right tabular-nums">{l.quantity}</td>
                  <td className="py-1 text-right tabular-nums">{formatDkk(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {invoice.postpone_reason ? (
        <div className="rounded bg-status-warn-tint p-2 text-xs text-status-warn">
          <span className="font-medium">Udskudt:</span> {invoice.postpone_reason}
          {invoice.postpone_until_date ? <> · til {invoice.postpone_until_date}</> : null}
          {invoice.postpone_mode === 'new_trigger_task' ? (
            <Pill variant="warning" className="ml-2">
              Ny trigger
            </Pill>
          ) : null}
        </div>
      ) : null}

      {error ? <div className="text-sm text-status-err">{error}</div> : null}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onApproveAndSend}
          className="rounded bg-status-err px-3 py-1.5 text-sm font-medium text-white hover:bg-status-err/90"
          disabled={!editable}
        >
          Godkend & marker som sendt
        </button>
        <button
          type="button"
          onClick={onApprove}
          className="rounded border border-status-err px-3 py-1.5 text-sm font-medium text-status-err hover:bg-status-err/5"
          disabled={!editable}
        >
          Kun godkend
        </button>
        <button
          type="button"
          onClick={onPostpone}
          className="rounded border border-line-2 px-3 py-1.5 text-sm text-primary hover:bg-paper-2"
          disabled={!editable}
        >
          Udskyd
        </button>
        {editable ? (
          <button
            type="button"
            onClick={save}
            className={cn(
              'rounded border border-accent px-3 py-1.5 text-sm text-accent hover:bg-accent/5',
              saving === 'save' && 'opacity-50',
            )}
            disabled={saving === 'save'}
          >
            {saving === 'save' ? 'Gemmer…' : 'Gem ændringer'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={cancel}
          className="ml-auto rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90"
          disabled={saving === 'cancel'}
        >
          {saving === 'cancel' ? 'Annullerer…' : 'Annullér'}
        </button>
      </div>
    </div>
  );
}
